// src/pages/Register.tsx
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/i18n";

export default function Register() {
  const { t } = useI18n();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ UI post-register (email confirm)
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>("");

  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Resolver next de forma segura (soporta URL-encoded)
  const safeNext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("next");
    if (!raw) return null;

    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }

    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;

    return decoded;
  }, [location.search]);

  // ✅ Si ya está logueado, no lo dejes en /signup (pero respeta next)
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;

    navigate(safeNext || "/mode", { replace: true });
  }, [user, authLoading, navigate, safeNext]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const name = form.name.trim();
    const email = form.email.trim();
    const password = form.password;

    if (!name) {
      setError(t("register.errors.nameRequired"));
      return;
    }

    if (!email) {
      setError(t("register.errors.emailInvalid"));
      return;
    }

    if (password.length < 6) {
      setError(t("register.errors.passwordMin"));
      return;
    }

    if (password !== form.confirmPassword) {
      setError(t("register.errors.passwordMismatch"));
      return;
    }

    setLoading(true);

    // ⚠️ MVP: por ahora solo enviamos email + password.
    // El nombre lo guardaremos luego en Perfil/Settings (cuando lo conectemos a DB).
    const result = await register(email, password);

    setLoading(false);

    if (!result.ok) {
      setError(t("register.errors.generic"));
      return;
    }

    // ✅ Si requiere confirmación por email, mostramos la pantalla de verificación
    if (result.needsEmailConfirm) {
      setRegisteredEmail(email);
      setNeedsEmailConfirm(true);
      return;
    }

    // ✅ Si NO requiere confirmación, ya hay sesión => ir a selector de modos o next
    navigate(safeNext || "/mode", { replace: true });
  };

  const GlowBg = () => (
    <>
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />
    </>
  );

  const EyeIcon = ({ off }: { off?: boolean }) =>
    off ? (
      // Eye-off
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M9.88 5.09A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a17.3 17.3 0 0 1-3.17 4.12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M6.11 6.11C3.77 7.74 2 12 2 12s3 7 10 7a10.23 10.23 0 0 0 3.89-.78"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ) : (
      // Eye
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

  // ✅ Pantalla post-registro (confirmación email)
  if (needsEmailConfirm) {
    return (
      <div className="relative min-h-screen bg-[#0b0b0f] flex items-center justify-center px-6 text-white overflow-hidden">
        <GlowBg />

        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/")}
            className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition"
            type="button"
          >
            <span aria-hidden>←</span> {t("register.backToHome")}
          </button>

          <div className="w-full bg-[#111114]/80 p-10 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-sm">
            <div className="text-center mb-6">
              <div className="text-3xl font-bold tracking-tight">
                Ovylon
                <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
              </div>
            </div>

            <h1 className="text-2xl font-semibold text-center">{t("register.confirm.title")}</h1>

            {/* ✅ Texto EXACTO como pediste: 1ra línea + email abajo completo */}
            <div className="mt-3 text-center leading-relaxed">
              <p className="text-sm text-gray-300">{t("register.confirm.sentTo")}</p>

              <p className="mt-1 text-sm">
                <span className="text-white font-semibold break-all">{registeredEmail}</span>
                <span className="text-white/80">.</span>
              </p>
            </div>

            <div className="mt-6 space-y-3 text-sm text-gray-400">
              <p>{t("register.confirm.step1")}</p>
              <p>{t("register.confirm.step2")}</p>
              <p className="text-[12px] text-gray-500">{t("register.confirm.tip")}</p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-3">
              <Link
                to={safeNext ? `/login?next=${encodeURIComponent(safeNext)}` : "/login"}
                className="w-full py-3 bg-white/10 border border-white/20 hover:bg-white/20 transition rounded-lg font-semibold text-center"
              >
                {t("register.confirm.ctaConfirmed")}
              </Link>

              <button
                type="button"
                onClick={() => {
                  setNeedsEmailConfirm(false);
                  setRegisteredEmail("");
                  setForm({ name: "", email: "", password: "", confirmPassword: "" });
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                  setError("");
                }}
                className="w-full py-3 bg-transparent border border-white/15 hover:bg-white/5 transition rounded-lg font-semibold text-center text-gray-200"
              >
                {t("register.confirm.ctaUseAnother")}
              </button>
            </div>

            <p className="mt-6 text-center text-gray-500 text-xs">
              {t("register.confirm.securityNote")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Formulario normal de registro
  return (
    <div className="relative min-h-screen bg-[#0b0b0f] flex items-center justify-center px-6 text-white overflow-hidden">
      <GlowBg />

      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition"
          type="button"
        >
          <span aria-hidden>←</span> {t("register.backToHome")}
        </button>

        <div className="w-full bg-[#111114]/80 p-10 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-sm">
          {/* Header marca (alineado con Login) */}
          <div className="text-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              Ovylon
              <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
            </div>

            {/* ✅ Texto en 2 líneas (como pediste) */}
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              {t("register.subtitleLine1")}
              <br />
              {t("register.subtitleLine2")}
            </p>
          </div>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs text-gray-400 mb-2">{t("register.nameLabel")}</label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                placeholder={t("register.namePlaceholder")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoComplete="given-name"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">{t("register.emailLabel")}</label>
              <input
                type="email"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                placeholder={t("register.emailPlaceholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">{t("register.passwordLabel")}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-14 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none appearance-none"
                  placeholder={t("register.passwordPlaceholder")}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-lpignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                  aria-label={showPassword ? t("register.hidePassword") : t("register.showPassword")}
                  title={showPassword ? t("register.hide") : t("register.show")}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">
                {t("register.confirmPasswordLabel")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-14 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none appearance-none"
                  placeholder={t("register.confirmPasswordPlaceholder")}
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-lpignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                  aria-label={
                    showConfirmPassword ? t("register.hidePassword") : t("register.showPassword")
                  }
                  title={showConfirmPassword ? t("register.hide") : t("register.show")}
                >
                  <EyeIcon off={showConfirmPassword} />
                </button>
              </div>
            </div>

            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            {/* ✅ Botón con el MISMO estilo del Login (principal) */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? t("register.creating") : t("register.submit")}
            </button>
          </form>

          <p className="mt-6 text-center text-gray-400">
            {t("register.alreadyHave")}{" "}
            <Link
              to={safeNext ? `/login?next=${encodeURIComponent(safeNext)}` : "/login"}
              className="text-white font-semibold hover:underline"
            >
              {t("register.loginLink")}
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">{t("register.footerNote")}</p>
      </div>
    </div>
  );
}



